import { 
  ChevronDown, 
  MoreHorizontal,
  LayoutDashboard,
  Layers,
  MessageCircle,
  BarChart3,
  FileText,
  Users,
  Megaphone,
  Sparkles,
  Gift,
  LucideIcon,
  Plus,
  Check,
  MessagesSquare,
  Bell,
  Moon,
  Sun
} from "lucide-react";
import { useTheme } from "next-themes";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/useProfileQuery";
import { useFeedbackBadges } from "@/hooks/useFeedbackBadges";
import { NotificationsPanel } from "@/components/dashboard/NotificationsPanel";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SidebarSearch } from "@/components/sidebar/SidebarSearch";
import { SubscriptionStatusBadge } from "@/components/sidebar/SubscriptionStatusBadge";
import { useNotifications } from "@/hooks/useNotifications";

import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useTour } from "@/contexts/TourContext";
import { SecondaryButton } from "@/components/StandardButton";
import { usePlanLimits } from "@/hooks/usePlanLimits";

// Lazy load heavy components
const LazyAIChatPanel = lazy(() =>
  import("@/components/ai-hub/AIChatPanel").then((m) => ({ default: m.AIChatPanel }))
);
const LazySidebarTrialBanner = lazy(() =>
  import("@/components/sidebar/SidebarTrialBanner").then((m) => ({ default: m.SidebarTrialBanner }))
);
const LazyPlatformFeedbackDialog = lazy(() =>
  import("@/components/PlatformFeedbackWidget").then((m) => ({ default: m.PlatformFeedbackDialog }))
);
const LazyUpgradeDialog = lazy(() =>
  import("@/components/ui/upgrade-dialog").then((m) => ({ default: m.UpgradeDialog }))
);

interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  dataTour?: string;
  requiresManager?: boolean;
}

// Core navigation items
const coreItems: NavItem[] = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Pages", url: "/dashboard/pages", icon: Layers, dataTour: "sidebar-pages" },
  { title: "Feedback", url: "/dashboard/feedback", icon: MessageCircle },
];

// Insights navigation items
const insightsItems: NavItem[] = [
  { title: "Analytics", url: "/dashboard/analytics", icon: BarChart3 },
  { title: "Reports", url: "/dashboard/reports", icon: FileText },
];

// Engage navigation items
const engageItems: NavItem[] = [
  { title: "Manager View", url: "/dashboard/manager", icon: Users, requiresManager: true },
  { title: "Campaigns", url: "/dashboard/campaigns", icon: Megaphone },
  { title: "AI Insights", url: "/dashboard/ai-hub", icon: Sparkles, dataTour: "sidebar-ai-insights" },
  { title: "Referrals", url: "/dashboard/referrals", icon: Gift },
];

// Special nav items (not standard navigation)
interface SpecialNavItem {
  title: string;
  icon: LucideIcon;
  action: "chat";
}

// Theme toggle menu item component
function ThemeMenuItem() {
  const { theme, setTheme } = useTheme();
  
  const cycleTheme = () => {
    if (theme === "light") setTheme("dark");
    else if (theme === "dark") setTheme("system");
    else setTheme("light");
  };
  
  const getThemeLabel = () => {
    if (theme === "dark") return "Dark";
    if (theme === "light") return "Light";
    return "System";
  };
  
  return (
    <DropdownMenuItem
      onClick={cycleTheme}
      className="py-2 px-2 cursor-pointer rounded-xl hover:bg-accent transition-colors text-sm flex items-center justify-between"
    >
      <span className="flex items-center gap-2">
        {theme === "dark" ? (
          <Moon className="h-4 w-4" />
        ) : (
          <Sun className="h-4 w-4" />
        )}
        Theme
      </span>
      <span className="text-muted-foreground text-xs">{getThemeLabel()}</span>
    </DropdownMenuItem>
  );
}

export function AppSidebar() {
  const { open } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useAdminAuth();
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { startTour } = useTour();

  const { data: profile } = useProfileQuery();
  const { getBadgeCount: getSharedBadgeCount } = useFeedbackBadges();
  const {
    notifications,
    unreadCount,
    handleMarkAsRead,
    handleMarkAllAsRead,
    handleDismissNotification,
    handleClearAll,
    soundEnabled,
    handleToggleSound,
  } = useNotifications();

  // Check if user is a manager
  const { data: isManager = false } = useQuery({
    queryKey: ["is-manager", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data, error } = await supabase
        .from("user_manager_roles")
        .select("id")
        .eq("user_id", user.id)
        .limit(1);
      if (error) throw error;
      return data && data.length > 0;
    },
    enabled: !!user?.id,
  });

  const isActive = (url: string) => {
    if (url === "/dashboard/settings") {
      return location.pathname.startsWith("/dashboard/settings");
    }
    return location.pathname === url;
  };

  const handleNavClick = async (itemTitle: string) => {
    if (!user) return;

    if (itemTitle === "Feedback") {
      await supabase
        .from("profiles")
        .update({ last_feedback_viewed_at: new Date().toISOString() })
        .eq("id", user.id);
    } else if (itemTitle === "Pages") {
      await supabase
        .from("profiles")
        .update({ last_pages_viewed_at: new Date().toISOString() })
        .eq("id", user.id);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "S";
    return name.charAt(0).toUpperCase();
  };

  const getBadgeCount = (itemTitle: string): number | null => {
    if (itemTitle === "Feedback") return getSharedBadgeCount("feedback");
    if (itemTitle === "Pages") return getSharedBadgeCount("pages");
    return null;
  };

  // Plan limits for new page creation
  const { plan, canCreatePage } = usePlanLimits();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const handleCreatePage = useCallback(async () => {
    const allowed = await canCreatePage();
    if (!allowed) {
      setShowUpgradeDialog(true);
    } else {
      navigate("/dashboard/new");
    }
  }, [canCreatePage, navigate]);

  // Keyboard shortcut for New Page (⌘N / Ctrl+N)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        handleCreatePage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreatePage]);

  // Filter navigation items based on permissions
  const filterItems = (items: NavItem[]) => items.filter(item => {
    if (item.requiresManager && !isManager) return false;
    return true;
  });

  const filteredEngageItems = filterItems(engageItems);

  // Render navigation item
  const renderNavItem = (item: NavItem) => {
    const badgeCount = getBadgeCount(item.title);
    const active = isActive(item.url);
    return (
      <SidebarMenuItem 
        key={item.title}
        data-tour={item.dataTour}
      >
        <SidebarMenuButton 
          asChild 
          isActive={active}
          tooltip={!open ? item.title : undefined}
          className={`group transition-colors rounded-2xl h-9 ${
            active 
              ? 'bg-accent text-foreground' 
              : 'hover:bg-accent text-foreground/80'
          }`}
        >
          <NavLink 
            to={item.url}
            onClick={() => handleNavClick(item.title)}
            className={`flex items-center gap-2.5 w-full ${!open ? 'justify-center px-0' : 'px-2'}`}
          >
            <item.icon 
              className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-foreground' : 'text-muted-foreground'}`} 
              strokeWidth={1.5}
            />
            {open && (
              <>
                <span className="truncate flex-1 text-[13px] font-medium">
                  {item.title}
                </span>
                {badgeCount && (
                  <Badge 
                    className="ml-auto h-5 min-w-5 px-1.5 text-[10px] flex items-center justify-center font-medium bg-destructive text-destructive-foreground rounded-full"
                    variant="default"
                  >
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </Badge>
                )}
              </>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar 
      collapsible="icon" 
      className="border-r border-border/50 bg-sidebar transition-all duration-200"
      data-tour="sidebar-navigation"
    >
      {/* Company Selector Header */}
      <SidebarHeader className={`py-3 ${open ? 'px-3' : 'px-2'}`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={`flex items-center gap-2 hover:bg-accent rounded-2xl p-1.5 transition-colors w-full ${!open ? 'justify-center' : ''}`}>
              <Avatar className="h-7 w-7 rounded-2xl bg-primary">
                <AvatarImage 
                  src={profile?.logo_url || undefined}
                  alt="Company"
                  className="object-cover rounded-2xl"
                />
                <AvatarFallback className="rounded-2xl bg-primary text-primary-foreground font-semibold text-xs">
                  {getInitials(profile?.company_name)}
                </AvatarFallback>
              </Avatar>
              {open && (
                <>
                  <span className="text-[13px] font-medium text-foreground truncate flex-1 text-left">
                    {profile?.company_name || "sendfeedback"}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 rounded-2xl bg-popover z-[100] shadow-lg border border-border"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            {/* Current Company - Selected */}
            <DropdownMenuItem
              className="py-2 px-2 cursor-default rounded-xl bg-accent/50 text-sm flex items-center gap-2"
            >
              <Avatar className="h-5 w-5 rounded-xl bg-primary">
                <AvatarImage 
                  src={profile?.logo_url || undefined}
                  className="object-cover rounded-xl"
                />
                <AvatarFallback className="rounded-xl bg-primary text-primary-foreground text-[10px]">
                  {getInitials(profile?.company_name)}
                </AvatarFallback>
              </Avatar>
              <span className="flex-1 truncate">
                {profile?.company_name || "sendfeedback"}
              </span>
              <Check className="h-4 w-4 text-primary" />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* Create New Company */}
            <DropdownMenuItem
              onClick={() => navigate("/dashboard/new-company")}
              className="py-2 px-2 cursor-pointer rounded-xl hover:bg-accent transition-colors text-sm flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create new company
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarHeader>

      {/* Quick Create Button + Search */}
      {open && (
        <div className="px-3 pb-2 space-y-2">
          <button
            onClick={handleCreatePage}
            className="w-full h-9 flex items-center justify-start px-3 text-[13px] bg-primary hover:bg-primary/90 text-primary-foreground border border-primary rounded-2xl transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Page
            <span className="ml-auto text-[11px] text-primary-foreground/70 font-normal">⌘N</span>
          </button>
          <SidebarSearch />
        </div>
      )}

      <SidebarContent className="gap-0 pt-2">
        {/* Core Navigation */}
        <SidebarGroup className={`py-0 ${open ? 'px-2' : 'px-1.5'}`}>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {coreItems.map(renderNavItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Insights Section */}
        <SidebarGroup className={`py-0 mt-4 ${open ? 'px-2' : 'px-1.5'}`}>
          {open && (
            <SidebarGroupLabel className="px-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
              Insights
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {insightsItems.map(renderNavItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Engage Section */}
        {filteredEngageItems.length > 0 && (
          <SidebarGroup className={`py-0 mt-4 ${open ? 'px-2' : 'px-1.5'}`}>
            {open && (
              <SidebarGroupLabel className="px-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Engage
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {filteredEngageItems.map(renderNavItem)}
                {/* AI Assistant - opens chat panel */}
                <SidebarMenuItem>
                  <SidebarMenuButton 
                    asChild
                    tooltip={!open ? "AI Assistant" : undefined}
                    className="group transition-colors rounded-2xl h-9 hover:bg-accent text-foreground/80"
                  >
                    <button
                      onClick={() => setChatOpen(true)}
                      data-tour="sidebar-ai-assistant"
                      className={`flex items-center gap-2.5 w-full ${!open ? 'justify-center px-0' : 'px-2'}`}
                    >
                      <MessagesSquare 
                        className="h-[18px] w-[18px] shrink-0 text-muted-foreground" 
                        strokeWidth={1.5}
                      />
                      {open && (
                        <span className="truncate flex-1 text-[13px] font-medium">
                          AI Assistant
                        </span>
                      )}
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>


      {/* Trial Banner - Lazy loaded */}
      <Suspense fallback={null}>
        <LazySidebarTrialBanner />
      </Suspense>

      {/* Footer with Avatar + Email + More Menu */}
      <SidebarFooter className={`mt-auto border-t border-border/30 py-2 ${open ? 'px-2' : 'px-1.5'}`}>
        <div className={`flex items-center gap-2 ${!open ? 'flex-col' : ''}`}>
          {/* Notification Bell */}
          <NotificationsPanel
            notifications={notifications}
            onMarkAsRead={handleMarkAsRead}
            onMarkAllAsRead={handleMarkAllAsRead}
            onDismiss={handleDismissNotification}
            onClearAll={handleClearAll}
            soundEnabled={soundEnabled}
            onToggleSound={handleToggleSound}
          >
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative h-7 w-7 hover:bg-accent rounded-xl shrink-0"
            >
              <Bell className="h-4 w-4 text-muted-foreground" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          </NotificationsPanel>

          {open && (
            <>
              <span className="text-[13px] text-foreground/80 truncate flex-1">
                {user?.email 
                  ? user.email.length > 22 
                    ? user.email.substring(0, 20) + "..." 
                    : user.email
                  : "user@email.com"}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7 hover:bg-accent rounded-xl shrink-0"
                  >
                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-48 rounded-2xl bg-popover z-[100] shadow-lg border border-border"
                  side="top"
                  align="end"
                  sideOffset={8}
                >
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard/settings")}
                    className="py-2 px-2 cursor-pointer rounded-xl hover:bg-accent transition-colors text-sm"
                  >
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard/billing")}
                    className="py-2 px-2 cursor-pointer rounded-xl hover:bg-accent transition-colors text-sm"
                  >
                    Billing
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={startTour}
                    className="py-2 px-2 cursor-pointer rounded-xl hover:bg-accent transition-colors text-sm"
                  >
                    Interactive Tour
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => window.open("/help", "_blank")}
                    className="py-2 px-2 cursor-pointer rounded-xl hover:bg-accent transition-colors text-sm"
                  >
                    Help & Support
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => navigate("/admins/dashboard")}
                        className="py-2 px-2 cursor-pointer rounded-xl hover:bg-accent transition-colors text-sm"
                      >
                        Admin Dashboard
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setFeedbackDialogOpen(true)}
                    className="py-2 px-2 cursor-pointer rounded-xl hover:bg-accent transition-colors text-sm"
                  >
                    Platform Feedback
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <ThemeMenuItem />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="py-2 px-2 cursor-pointer rounded-xl hover:bg-accent text-destructive focus:text-destructive focus:bg-destructive/10 transition-colors text-sm"
                  >
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </SidebarFooter>

      {/* Lazy loaded dialogs and panels */}
      {feedbackDialogOpen && (
        <Suspense fallback={null}>
          <LazyPlatformFeedbackDialog 
            open={feedbackDialogOpen} 
            onOpenChange={setFeedbackDialogOpen} 
          />
        </Suspense>
      )}

      {chatOpen && (
        <Suspense fallback={null}>
          <LazyAIChatPanel open={chatOpen} onOpenChange={setChatOpen} />
        </Suspense>
      )}

      {showUpgradeDialog && (
        <Suspense fallback={null}>
          <LazyUpgradeDialog
            open={showUpgradeDialog}
            onOpenChange={setShowUpgradeDialog}
            feature="more feedback pages"
            requiredPlan={plan === "starter" ? "professional" : "business"}
            currentPlan={plan as any}
          />
        </Suspense>
      )}
    </Sidebar>
  );
}
